import { useEffect, useState } from "react";
import { listCategories, createCategory, updateCategory, deleteCategory, listCategoryRequests, reviewCategoryRequest } from "@/lib/firestore";
import { CAT } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash, PencilSimple, Plus, X, CheckCircle, XCircle } from "@phosphor-icons/react";

export default function Categories() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");

  const load = async () => {
    const [data, reqs] = await Promise.all([listCategories(), listCategoryRequests(user)]);
    setItems(data);
    setRequests(reqs.filter((r) => r.status === "pending"));
  };
  useEffect(() => { load(); }, []);

  const review = async (id, action) => {
    try {
      await reviewCategoryRequest(id, action, user);
      toast.success(action === "approve" ? "Category created" : "Request rejected");
      await load();
    } catch (e) { toast.error(e.message); }
  };

  const openNew = () => { setEditing(null); setName(""); setDesc(""); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setName(c.name); setDesc(c.description || ""); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateCategory(editing.id, { name, description: desc });
        toast.success("Category updated");
      } else {
        await createCategory({ name, description: desc }, user);
        toast.success("Category created");
      }
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    try {
      await deleteCategory(id);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-xs text-[#FF4F00] tracking-widest mb-2">// TAXONOMY</div>
          <h1 className="font-display font-black text-4xl">CATEGORIES</h1>
          <div className="text-[#A1A1AA] mt-1 text-sm">Define your own tool categories.</div>
        </div>
        <button
          data-testid={CAT.addBtn}
          onClick={openNew}
          className="brutal-btn bg-[#FF4F00] hover:bg-[#FF6B2B] text-black px-4 py-2.5 flex items-center gap-2 transition-colors duration-150"
        >
          <Plus size={16} weight="bold" /> New Category
        </button>
      </div>

      {requests.length > 0 && (
        <div className="brutal-card p-5 border-l-4 border-l-[#FFD700]">
          <div className="flex items-center gap-3 mb-3">
            <h2 className="font-display font-black text-lg">PENDING REQUESTS</h2>
            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border border-[#FFD700] text-[#FFD700]">{requests.length}</span>
          </div>
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#1a1a1e] pt-2 first:border-t-0 first:pt-0">
                <div className="text-sm">
                  <span className="font-semibold">{r.name}</span>
                  <span className="text-[#52525B] font-mono text-xs"> · requested by {r.requested_by_name}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => review(r.id, "approve")}
                    className="brutal-btn border border-[#00E676] text-[#00E676] hover:bg-[#00E676] hover:text-black px-3 py-1.5 flex items-center gap-1 text-xs transition-colors duration-150"
                  >
                    <CheckCircle size={12} weight="bold" /> Approve
                  </button>
                  <button
                    onClick={() => review(r.id, "reject")}
                    className="brutal-btn border border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F] hover:text-white px-3 py-1.5 flex items-center gap-1 text-xs transition-colors duration-150"
                  >
                    <XCircle size={12} weight="bold" /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="brutal-card p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="font-mono text-xs text-[#FF4F00] tracking-widest">
              // {editing ? "EDIT" : "NEW"} CATEGORY
            </div>
            <button onClick={() => setShowForm(false)} className="text-[#A1A1AA] hover:text-white"><X size={18} /></button>
          </div>
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              data-testid={CAT.nameInput}
              placeholder="Name (e.g., Cutting Tools)"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00] transition-colors duration-150"
            />
            <input
              data-testid={CAT.descInput}
              placeholder="Description (optional)"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              className="bg-[#0A0A0C] border border-[#27272A] px-3 py-2.5 focus:outline-none focus:border-[#FF4F00] transition-colors duration-150"
            />
            <div className="md:col-span-2 flex gap-2">
              <button
                type="submit"
                data-testid={CAT.saveBtn}
                className="brutal-btn bg-[#FF4F00] text-black px-4 py-2.5 hover:bg-[#FF6B2B] transition-colors duration-150"
              >
                {editing ? "Update" : "Create"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="brutal-btn border border-[#27272A] px-4 py-2.5 hover:border-[#FF4F00] transition-colors duration-150">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full brutal-card p-8 text-center text-[#52525B] font-mono">
            // no categories yet — create one to organize your tools //
          </div>
        )}
        {items.map((c) => (
          <div key={c.id} className="brutal-card p-5">
            <div className="flex justify-between items-start mb-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF4F00]">CATEGORY</div>
              <div className="flex gap-1">
                <button
                  data-testid={CAT.editBtn(c.id)}
                  onClick={() => openEdit(c)}
                  className="w-8 h-8 border border-[#27272A] hover:border-[#FF4F00] hover:text-[#FF4F00] flex items-center justify-center transition-colors duration-150"
                >
                  <PencilSimple size={14} />
                </button>
                {user?.role === "admin" && (
                  <button
                    data-testid={CAT.deleteBtn(c.id)}
                    onClick={() => del(c.id)}
                    className="w-8 h-8 border border-[#27272A] hover:border-[#D32F2F] hover:text-[#D32F2F] flex items-center justify-center transition-colors duration-150"
                  >
                    <Trash size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="font-display font-bold text-xl mb-1">{c.name}</div>
            <div className="text-sm text-[#A1A1AA]">{c.description || "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
